import { useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'src/redux-app-hooks';

import { ProductRequirementGroup, RequirementFieldOption } from '../data-collection.dto';
import { RealEstateAsset } from '../real-estate-asset-management.dto';
import { DataCollectionTab } from './DataCollectionTab';
import {
    activeProductIds,
    enabledServiceInfoGroupCodes,
    serviceInformationGroups,
} from './service-information.helpers';

const TAB_SX = { minHeight: 40 };

/**
 * Upcoming service-information groups shown as disabled tabs until they are
 * configured (mirrors the GEN-591 mock). A configured group with the same
 * name replaces its placeholder.
 */
const PLACEHOLDER_TABS = ['BoQ', 'Energy audit'];

/**
 * Services → Information (GEN-591): per-service-group data-collection forms.
 * One inner tab per SERVICE_INFORMATION requirement group (e.g. "PA & MAKS"),
 * enabled only while a matching service is connected. The form itself is the
 * ordinary config-driven `DataCollectionTab` — the backend routes these
 * SERVICE-entity answers to the per-service store and fans them out to every
 * active PA/MAKS service on the REA.
 */
export function ServiceInformationTab({
    realEstateAsset,
    groups,
    buildingOptions,
    onDirtyChange,
}: {
    realEstateAsset: RealEstateAsset;
    groups: ProductRequirementGroup[];
    buildingOptions: RequirementFieldOption[];
    onDirtyChange?: (dirty: boolean) => void;
}) {
    const { t } = useTranslation();
    const { reaServices } = useAppSelector((state) => state.realEstateAssetManagement);

    const serviceInfoGroups = useMemo(() => serviceInformationGroups(groups), [groups]);

    const activeProducts = useMemo(() => activeProductIds(reaServices), [reaServices]);

    const enabledCodes = useMemo(
        () => enabledServiceInfoGroupCodes(serviceInfoGroups, activeProducts),
        [serviceInfoGroups, activeProducts],
    );

    const [selectedCode, setSelectedCode] = useState<string | null>(null);
    // Fall back to the first enabled group when nothing (or something now
    // disabled, e.g. after the service was disconnected) is selected.
    const effectiveCode =
        selectedCode && enabledCodes.has(selectedCode)
            ? selectedCode
            : (serviceInfoGroups.find((g) => enabledCodes.has(g.code))?.code ?? null);
    const activeGroup = serviceInfoGroups.find((g) => g.code === effectiveCode) ?? null;

    const placeholders = PLACEHOLDER_TABS.filter((label) => !serviceInfoGroups.some((g) => g.name === label));

    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                {/* value={false} keeps MUI happy while no group is selectable.
                    onChange on Tabs (not per-Tab onClick) keeps MUI's keyboard
                    navigation working; disabled tabs can't be selected. */}
                <Tabs value={effectiveCode ?? false} onChange={(_, value) => setSelectedCode(value)} sx={TAB_SX}>
                    {serviceInfoGroups.map((g) => (
                        <Tab
                            key={g.code}
                            value={g.code}
                            label={t(g.name)}
                            disabled={!enabledCodes.has(g.code)}
                            sx={TAB_SX}
                        />
                    ))}
                    {placeholders.map((label) => (
                        <Tab key={label} value={`placeholder:${label}`} label={t(label)} disabled sx={TAB_SX} />
                    ))}
                </Tabs>
            </Box>
            {activeGroup ? (
                <DataCollectionTab
                    realEstateAssetId={realEstateAsset.id}
                    groupCode={activeGroup.code}
                    reaServices={reaServices}
                    buildingOptions={buildingOptions}
                    groups={groups}
                    onDirtyChange={onDirtyChange}
                />
            ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('Connect a service to collect its delivery information.')}
                </Typography>
            )}
        </Box>
    );
}
